import { Container, Graphics, Sprite } from 'pixi.js';
import type { CupsLifted, CupsOffer, CupsState, CupsSwaps } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { canPick, cupPositions, cupsExplain, cupsLead, isLifted, swapMs } from '../../logic/minigames/cups';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { ROAD_SOUNDS } from '../../logic/road/sounds';
import { animate, ease, prefersReducedMotion } from '../board/ui';
import { glowTexture } from '../road/glow';
import { TurnGameDialog } from './TurnGameDialog';
import type { MiniGameDeps } from './types';
import type { GameView } from '../../logic/minigames/core';

type CupsReveal = CupsSwaps | CupsLifted;

/** How high a lifted cup rises, as a fraction of its height. */
const LIFT = 0.5;

interface CupView {
  root: Container;
  shadow: Graphics;
  body: Container;
  sprite: Sprite;
  drawn: Graphics;
  /** Where it stands (a position, fractional while it moves). */
  slot: number;
  /** 0 down, 1 lifted. */
  lift: number;
  /** In front (1) or behind (-1) while it passes another cup. */
  depth: number;
}

/**
 * Find the coins: three cups. SHUFFLE shows the coins go under a cup, then
 * the cups swap places, one swap at a time and quicker as they go (the
 * server drew the swaps and sends them now). Tap the cup with the coins;
 * a miss lifts that cup empty, then the one they were under. Finding them
 * pays the prize; a miss pays a little, said plainly as a loss.
 */
export class CupsDialog extends TurnGameDialog<CupsOffer, CupsState, CupsReveal> {
  private cups: CupView[] = [];
  private readonly coins = new Container();
  private readonly coinsSprite = new Sprite();
  private readonly coinsDrawn = new Graphics();
  private readonly sparkle: Sprite;
  /** The position the coins are at, while showing. */
  private coinsAt = 0;
  private shuffling = false;
  private metrics = { cx: 0, spacing: 100, base: 200, w: 80, h: 120 };

  constructor(deps: MiniGameDeps) {
    super(deps, 'cups', 'FIND THE COINS');
    this.coinsSprite.anchor.set(0.5, 1);
    this.sparkle = new Sprite(glowTexture());
    this.sparkle.anchor.set(0.5);
    this.sparkle.blendMode = 'add';
    this.sparkle.tint = 0xffd36b;
    this.sparkle.visible = false;
    this.coins.addChild(this.sparkle, this.coinsDrawn, this.coinsSprite);
    this.coins.eventMode = 'none';
  }

  protected begin(offer: CupsOffer): void {
    this.cups.forEach((c) => c.root.destroy({ children: true }));
    this.content.addChild(this.coins);
    this.coins.visible = false;
    this.sparkle.visible = false;
    this.shuffling = false;
    const n = offer.setup?.cups ?? 3;
    this.cups = Array.from({ length: n }, (_, i) => {
      const root = new Container();
      const shadow = new Graphics();
      const body = new Container();
      const sprite = new Sprite();
      sprite.anchor.set(0.5, 1);
      const drawn = new Graphics();
      body.addChild(drawn, sprite);
      root.addChild(shadow, body);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      root.on('pointertap', () => this.onCup(root));
      this.content.addChild(root);
      return { root, shadow, body, sprite, drawn, slot: i, lift: 0, depth: 0 };
    });
  }

  /** A cup was tapped: the position it stands at now. */
  private onCup(root: Container): void {
    const cup = this.cups.find((c) => c.root === root);
    if (cup) this.onPiece(Math.round(cup.slot));
  }

  protected onPiece(position: number): void {
    if (canPick(this.game, this.shuffling)) {
      const cup = this.cupAt(position);
      if (!cup) return;
      this.settling = true;
      this.act('cup', String(position), position);
      void this.wobble(cup);
      return;
    }
    if (this.game.phase === 'play' && !this.game.state) {
      const cup = this.cupAt(position);
      if (cup) this.explain(cupsExplain(this.offer?.setup), { x: cup.root.x, y: cup.root.y - this.metrics.h * 0.25 });
    }
  }

  protected override actionLabel(): string | null {
    return this.game.state || this.shuffling ? null : 'SHUFFLE';
  }

  protected override onActionTap(): void {
    if (this.shuffling || this.game.state) return;
    this.shuffling = true;
    this.act('shuffle');
  }

  protected override wonPrize(): boolean {
    return !!this.game.state?.lifted?.found;
  }

  /** The board keeps still when SHUFFLE goes and the line under the
   *  title changes, so the cups don't jump. */
  protected override keepButtonRoom(): boolean {
    return this.game.phase !== 'over';
  }

  protected override leadMinLines(): number {
    return this.game.phase !== 'over' ? 2 : 1;
  }

  protected override onAnswered(prev: GameView<CupsState, CupsReveal>, next: GameView<CupsState, CupsReveal>): void {
    const last = next.last;
    if (last && last !== prev.last && !next.error) {
      if (isLifted(last)) void this.lift(last);
      else void this.shuffle(last);
      return;
    }
    // Refused, lost, or caught up with the server without its answer:
    // nothing to animate, so let the player go on (or see how it ended).
    this.shuffling = false;
    this.settling = false;
    const lifted = next.state?.lifted;
    if (next.phase === 'over' && lifted) {
      this.coinsAt = lifted.prizeAt;
      this.coins.visible = true;
      this.cups.forEach((c) => (c.lift = Math.round(c.slot) === lifted.prizeAt || Math.round(c.slot) === lifted.cup ? 1 : 0));
      this.place();
    }
  }

  /** Show the coins go under their cup, then run the swaps. */
  private async shuffle(s: CupsSwaps): Promise<void> {
    this.shuffling = true;
    this.render();
    const start = this.cupAt(s.start);
    this.coinsAt = s.start;
    this.coins.visible = true;
    this.place();
    if (start) {
      await this.raise(start, 1, 260);
      await wait(650);
      await this.raise(start, 0, 220);
    }
    this.coins.visible = false;
    await wait(200);
    const reduced = prefersReducedMotion();
    for (let i = 0; i < s.swaps.length; i++) {
      const [a, b] = s.swaps[i]!;
      const ca = this.cupAt(a);
      const cb = this.cupAt(b);
      if (!ca || !cb) continue;
      AudioBus.shared.play(GAME_SOUNDS.cupSlide, { volume: 0.7 });
      const ms = reduced ? 0 : swapMs(i, s.swaps.length);
      // The cup moving right passes in front.
      const front = a < b ? ca : cb;
      const back = front === ca ? cb : ca;
      front.depth = 1;
      back.depth = -1;
      this.sortCups();
      await animate(ms, (t) => {
        const k = 0.5 - 0.5 * Math.cos(Math.PI * t);
        ca.slot = a + (b - a) * k;
        cb.slot = b + (a - b) * k;
        const arc = Math.sin(Math.PI * t);
        front.depth = arc;
        back.depth = -arc;
        this.place();
      });
      ca.slot = b;
      cb.slot = a;
      ca.depth = cb.depth = 0;
      this.place();
      await wait(reduced ? 0 : 50);
    }
    // Where each cup is, as the server will count it.
    const at = cupPositions(this.cups.length, s.swaps);
    this.cups.forEach((c, i) => (c.slot = at[i] ?? c.slot));
    this.shuffling = false;
    this.render();
  }

  /** The pick: lift the cup; on a miss, then the one with the coins. */
  private async lift(l: CupsLifted): Promise<void> {
    this.coinsAt = l.prizeAt;
    this.coins.visible = true;
    this.sparkle.visible = l.found;
    this.place();
    const picked = this.cupAt(l.cup);
    if (picked) await this.raise(picked, 1, 300);
    AudioBus.shared.play(ROAD_SOUNDS.hiloFlip, { volume: 0.8 });
    if (!l.found) {
      await wait(450);
      const real = this.cupAt(l.prizeAt);
      if (real) await this.raise(real, 1, 300);
    } else {
      void animate(500, (t) => (this.sparkle.alpha = 0.9 * Math.sin(t * Math.PI * 0.5)));
    }
    await wait(250);
    this.reveal();
  }

  private async raise(cup: CupView, to: number, ms: number): Promise<void> {
    const from = cup.lift;
    await animate(prefersReducedMotion() ? 0 : ms, (t) => {
      cup.lift = from + (to - from) * (to > from ? ease.outCubic(t) : t * t);
      this.place();
    });
  }

  private async wobble(cup: CupView): Promise<void> {
    await animate(180, (t) => (cup.body.rotation = Math.sin(t * Math.PI * 2) * 0.06));
    cup.body.rotation = 0;
  }

  private cupAt(position: number): CupView | undefined {
    return this.cups.find((c) => Math.round(c.slot) === position);
  }

  /** Cups passing in front draw over the others. */
  private sortCups(): void {
    [...this.cups].sort((p, q) => p.depth - q.depth).forEach((c) => this.content.addChild(c.root));
    this.keepExplainerOnTop();
  }

  protected leadText(): string {
    if (this.settling && this.game.phase === 'over') return 'Lifting the cup…';
    return cupsLead(this.game, this.offer?.setup, this.shuffling);
  }

  protected pieceLabels(): string[] {
    const lifted = this.game.state?.lifted;
    const shown = this.game.phase === 'over' && !this.settling && lifted;
    return this.cups.map((_, p) => {
      if (!shown) return `Cup ${p + 1}`;
      if (p === lifted.prizeAt) return `Cup ${p + 1}: coins`;
      return p === lifted.cup ? `Cup ${p + 1}: empty` : `Cup ${p + 1}`;
    });
  }

  protected renderPieces(y: number, width: number, maxHeight: number): number {
    const n = this.cups.length || 3;
    // Cups are half as tall again as they are wide; room above for a lift.
    const byWidth = (width / n) * 0.74;
    const byHeight = (maxHeight - 20) / (1.5 * (1 + LIFT));
    const w = Math.max(48, Math.min(112, byWidth, byHeight));
    const h = w * 1.5;
    const top = y + h * LIFT + 8;
    this.metrics = { cx: width / 2, spacing: Math.min(width / n, w * 1.4), base: top + h, w, h };
    const cupTex = this.deps.texture('road:cups_cup');
    this.cups.forEach((c) => {
      c.drawn.clear();
      if (cupTex) {
        c.sprite.texture = cupTex;
        c.sprite.width = w;
        c.sprite.height = h;
        c.sprite.visible = true;
      } else {
        c.sprite.visible = false;
        c.drawn
          .poly([-w * 0.32, -h, w * 0.32, -h, w * 0.48, -h * 0.04, -w * 0.48, -h * 0.04])
          .fill({ color: 0xc8542a })
          .stroke({ color: 0x0b1838, width: 3 });
      }
      c.root.hitArea = { contains: (x: number, yy: number) => Math.abs(x) <= w * 0.5 && yy >= -h && yy <= 0 };
      c.root.cursor = canPick(this.game, this.shuffling) ? 'pointer' : 'default';
    });
    const coinTex = this.deps.texture('road:cups_coins');
    this.coinsDrawn.clear();
    this.coinsSprite.visible = !!coinTex;
    if (coinTex) {
      this.coinsSprite.texture = coinTex;
      this.coinsSprite.width = this.coinsSprite.height = w * 0.86;
    } else {
      this.coinsDrawn.ellipse(0, -w * 0.12, w * 0.32, w * 0.12).fill({ color: 0xf4c430 }).stroke({ color: 0x0b1838, width: 2 });
    }
    this.sparkle.width = this.sparkle.height = w * 1.3;
    this.sparkle.y = -w * 0.3;
    this.place();
    return top + h + 10;
  }

  /** Put every cup (and the coins) where its slot, lift and depth say. */
  private place(): void {
    const { cx, spacing, base, w, h } = this.metrics;
    const mid = (this.cups.length - 1) / 2;
    this.cups.forEach((c) => {
      const scale = 1 + 0.06 * c.depth;
      c.root.position.set(cx + (c.slot - mid) * spacing, base + c.depth * w * 0.1);
      c.root.scale.set(scale);
      c.body.y = -c.lift * h * LIFT;
      c.shadow.clear().ellipse(0, 0, w * (0.42 - 0.08 * c.lift), w * 0.08).fill({ color: 0x000000, alpha: 0.38 - 0.15 * c.lift });
    });
    this.coins.position.set(cx + (this.coinsAt - mid) * spacing, base - 2);
  }
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, prefersReducedMotion() ? 0 : ms));
